import { createSlice } from "@reduxjs/toolkit";

import api from "../../api/api";
import endpoints from "../../api/endpoints";
import ToastNotification from "../../common/toast/Toast";

// ---------- storage helpers ----------
const LS_KEY = "cart";

// Helper to normalize cart items, ensuring selected is always a boolean
const normalizeCartItems = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    ...item,
    selected: item.selected === true || item.selected === "true",
  }));
};

const loadCartFromStorage = async (
  userInfo = null,
  getAccessTokenSilently = null
) => {
  try {
    if (userInfo && userInfo.id) {
      // Parse localStorage
      const localData = parseCartData(localStorage.getItem(LS_KEY));

      // Parse backend data
      const backendRaw =
        userInfo.custentity_cart_item &&
        userInfo.custentity_cart_item.trim().length > 0
          ? userInfo.custentity_cart_item
          : null;
      const backendData = parseCartData(backendRaw);

      // Pick the right source
      let chosen;
      if (backendData.items.length > 0) {
        // Backend has items — always use backend
        chosen = backendData;
      } else {
        // Backend is empty — compare lastModified to decide
        if (localData.lastModified && backendData.lastModified) {
          chosen =
            new Date(localData.lastModified) >=
            new Date(backendData.lastModified)
              ? localData
              : backendData;
        } else if (backendData.lastModified) {
          chosen = backendData;
        } else if (localData.lastModified) {
          chosen = localData;
        } else {
          chosen = localData;
        }
      }

      // Sync chosen data back to localStorage
      saveCartToStorage(chosen.items, chosen.lastModified);

      return {
        items: normalizeCartItems(chosen.items),
        lastModified: chosen.lastModified,
      };
    } else {
      return { items: [], lastModified: null };
    }
  } catch {
    return { items: [], lastModified: null };
  }
};

const saveCartToStorage = (items, lastModified) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ items, lastModified }));
  } catch {}
};

// Parse cart data from JSON string (handles both legacy array and new {items, lastModified} format)
const parseCartData = (raw) => {
  if (!raw) return { items: [], lastModified: null };
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { items: parsed, lastModified: null };
    }
    if (parsed && typeof parsed === "object" && "items" in parsed) {
      return {
        items: Array.isArray(parsed.items) ? parsed.items : [],
        lastModified: parsed.lastModified || null,
      };
    }
    return { items: [], lastModified: null };
  } catch {
    return { items: [], lastModified: null };
  }
};

// Create audit log for cart item changes
const createCartAuditLog = async (items, userId) => {
  try {
    if (!userId || !items || items.length === 0) return;

    // Serialize BEFORE the first await. This is called from inside an Immer
    // producer, so `items` is a draft that Immer revokes once the producer
    // returns — reading it after an await throws on the revoked proxy.
    const cartJson = JSON.stringify(items);

    // The audit endpoint requires auth and only accepts the caller's own id.
    const token = tokenGetter ? await tokenGetter() : null;
    if (!token) return;

    const auditName = `Cart Audit - User ${userId}`;

    await api.post(
      endpoints.POST_CREATE_CART_ITEM_AUDIT(),
      {
        name: auditName,
        custrecord_user: userId,
        custrecord_cart_item: cartJson,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (error) {
    // Silently fail - audit log is not critical
  }
};

// Debounced sync function - only sync the LAST action after user stops
let syncTimeout = null;

const syncToBackendWithTimeout = () => {
  // Clear the previous timeout if it exists
  if (syncTimeout) {
    clearTimeout(syncTimeout);
  }

  // Set a new timeout - this will only execute if no new actions happen within 1 second
  syncTimeout = setTimeout(async () => {
    try {
      if (!storeGetState) return;

      const state = storeGetState();
      const userInfo = state?.user?.info;
      const localStorageData = localStorage.getItem(LS_KEY);

      if (userInfo?.id && localStorageData) {
        // Get access token if available
        const token = tokenGetter ? await tokenGetter() : null;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        api
          .patch(
            endpoints.PATCH_UPDATE_CUSTOMER(),
            {
              custentity_cart_item: localStorageData,
            },
            { headers }
          )
          .catch(() => {
            // Silently fail - cart will still work with localStorage
          });
      }
    } catch {
      // Silently fail
    }
  }, 1000); // 1 second debounce delay
};

// Store reference to getState and getAccessTokenSilently for backend sync
let storeGetState = null;
let storeDispatch = null;
let tokenGetter = null;

export const setStoreRef = (store, getAccessTokenSilently = null) => {
  storeGetState = store.getState;
  // Toast action buttons ("View cart", "Undo") dispatch at CLICK time — long
  // after the reducer that showed the toast has returned — so a dispatch handle
  // is legal there even though the toast itself is (regrettably, see backlog)
  // fired from inside reducers.
  storeDispatch = store.dispatch;
  tokenGetter = getAccessTokenSilently;
};

// ---------- utils ----------
const findItem = (list, id) => list.find((i) => i.id === id);

const normalizeSubscription = (payload = {}) => {
  const {
    custrecord_ci_sub_enabled = false,
    custrecord_ci_sub_interval = null,
    custrecord_ci_sub_unit = "months",
  } = payload;

  return custrecord_ci_sub_enabled
    ? {
        custrecord_ci_sub_enabled: true,
        custrecord_ci_sub_interval: String(custrecord_ci_sub_interval || "1"),
        custrecord_ci_sub_unit,
      }
    : {
        custrecord_ci_sub_enabled: false,
        custrecord_ci_sub_interval: null,
        custrecord_ci_sub_unit,
      };
};

// ---------- slice ----------
const cartSlice = createSlice({
  name: "Cart",
  initialState: {
    items: [], // Initialize as empty array
    loading: false,
    lastModified: null,
    // UI state for the header's slide-in cart drawer. Deliberately NOT persisted —
    // saveCartToStorage writes only { items, lastModified } — so a reload never
    // reopens the drawer.
    drawerOpen: false,
  },
  reducers: {
    addToCart: (state, action) => {
      const { id, quantity: rawQty = 1, userId, ...rest } = action.payload;

      const qtyNum = Number(rawQty);
      const qty = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 1;

      const existing = findItem(state.items, id);
      const sub = normalizeSubscription(rest);

      if (existing) {
        existing.quantity += qty;

        // honor explicit subscription fields if provided
        if (
          Object.prototype.hasOwnProperty.call(
            rest,
            "custrecord_ci_sub_enabled"
          )
        ) {
          existing.custrecord_ci_sub_enabled = sub.custrecord_ci_sub_enabled;
          existing.custrecord_ci_sub_interval = sub.custrecord_ci_sub_interval;
          existing.custrecord_ci_sub_unit = sub.custrecord_ci_sub_unit;
        }

        // honor explicit selected if provided
        if (Object.prototype.hasOwnProperty.call(rest, "selected")) {
          existing.selected = !!rest.selected;
        }
      } // addToCart reducer (only the object pushed in the "else" branch changed)
      else {
        state.items.push({
          ...action.payload,
          quantity: qty,
          custrecord_ci_sub_enabled: sub.custrecord_ci_sub_enabled,
          custrecord_ci_sub_interval: sub.custrecord_ci_sub_interval,
          custrecord_ci_sub_unit: sub.custrecord_ci_sub_unit,
          // ✅ default to selected when not explicitly provided
          selected:
            Object.prototype.hasOwnProperty.call(rest, "selected") &&
            typeof rest.selected === "boolean"
              ? !!rest.selected
              : true,
        });
      }

      // Reference toast shape: bold title, product in the subtitle, and a
      // "View cart" action that opens the drawer (the reference's `openCart`).
      const itemid = action.payload.itemid || action.payload.id;
      ToastNotification.success("Added to cart", {
        sub: qty > 1 ? `${qty} × ${itemid}` : String(itemid),
        duration: 3400,
        action: storeDispatch
          ? {
              label: "View cart",
              onClick: () => storeDispatch(cartSlice.actions.openCartDrawer()),
            }
          : undefined,
      });

      state.lastModified = new Date().toISOString();
      saveCartToStorage(state.items, state.lastModified);
      syncToBackendWithTimeout();
    },

    removeFromCart: (state, action) => {
      const removed = state.items.find((item) => item.id === action.payload);
      state.items = state.items.filter((item) => item.id !== action.payload);
      state.lastModified = new Date().toISOString();
      saveCartToStorage(state.items, state.lastModified);
      syncToBackendWithTimeout();

      // Reference `_removeCartId`: removal confirms with a one-tap Undo instead
      // of asking first. Spread = plain snapshot NOW — the Immer draft `removed`
      // is revoked once this reducer returns, so the click-time closure must not
      // touch it.
      if (removed) {
        const line = { ...removed };
        ToastNotification.success("Removed from cart", {
          sub: line.itemid ? String(line.itemid) : "",
          duration: 3600,
          action: storeDispatch
            ? {
                label: "Undo",
                muted: true,
                onClick: () => {
                  storeDispatch(cartSlice.actions.undoRemoveFromCart(line));
                  ToastNotification.dismiss();
                },
              }
            : undefined,
        });
      }
    },

    // The Undo path for removeFromCart's toast: restores the exact line
    // (quantity, subscription fields and all) WITHOUT the "Added to cart"
    // toast — the reference's `undoCartRemove` restores silently and just
    // dismisses. Skips if the item was re-added in the meantime.
    undoRemoveFromCart: (state, action) => {
      const line = action.payload;
      if (!line || state.items.some((item) => item.id === line.id)) return;
      state.items.push(line);
      state.lastModified = new Date().toISOString();
      saveCartToStorage(state.items, state.lastModified);
      syncToBackendWithTimeout();
    },

    clearCart: (state) => {
      state.items = [];
      state.lastModified = new Date().toISOString();
      saveCartToStorage(state.items, state.lastModified);
      syncToBackendWithTimeout();
    },

    loadCart: (state) => {
      // Synchronous fallback - load from localStorage only
      try {
        const { items, lastModified } = parseCartData(
          localStorage.getItem(LS_KEY)
        );
        state.items = items.map((item) => ({
          ...item,
          selected: item.selected === true || item.selected === "true",
        }));
        state.lastModified = lastModified;
      } catch {
        state.items = [];
      }
    },

    setLoading: (state, action) => {
      state.loading = !!action.payload;
    },

    updateQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const qtyNum = Number(quantity);
      const item = findItem(state.items, id);

      if (item && Number.isFinite(qtyNum) && qtyNum > 0) {
        item.quantity = qtyNum;
        state.lastModified = new Date().toISOString();
        saveCartToStorage(state.items, state.lastModified);
        syncToBackendWithTimeout();
      }
    },

    setItemSubscription: (state, action) => {
      const { id, enabled, interval, unit } = action.payload;
      const item = findItem(state.items, id);
      if (!item) return;

      const sub = normalizeSubscription({
        custrecord_ci_sub_enabled: enabled,
        custrecord_ci_sub_interval:
          interval ?? item.custrecord_ci_sub_interval ?? "1",
        custrecord_ci_sub_unit: unit ?? item.custrecord_ci_sub_unit ?? "months",
      });

      item.custrecord_ci_sub_enabled = sub.custrecord_ci_sub_enabled;
      item.custrecord_ci_sub_interval = sub.custrecord_ci_sub_interval;
      item.custrecord_ci_sub_unit = sub.custrecord_ci_sub_unit;

      state.lastModified = new Date().toISOString();
      saveCartToStorage(state.items, state.lastModified);
      syncToBackendWithTimeout();
    },

    // ⬇️ NEW: set selection per item (persisted)
    setItemSelected: (state, action) => {
      const { id, selected } = action.payload || {};
      const item = findItem(state.items, id);
      if (item) {
        item.selected = !!selected;
        state.lastModified = new Date().toISOString();
        saveCartToStorage(state.items, state.lastModified);
        syncToBackendWithTimeout();
      }
    },

    // (kept) replace items if needed elsewhere
    setItems: (state, action) => {
      // Accept either an array (legacy) or { items, lastModified }
      let items, lastModified;
      if (Array.isArray(action.payload)) {
        items = action.payload;
        lastModified = null;
      } else {
        items = Array.isArray(action.payload?.items)
          ? action.payload.items
          : [];
        lastModified = action.payload?.lastModified || null;
      }
      // Normalize selected property to ensure it's always a boolean
      state.items = items.map((item) => ({
        ...item,
        selected: item.selected === true || item.selected === "true",
      }));
      state.lastModified = lastModified;
      saveCartToStorage(state.items, state.lastModified);
      syncToBackendWithTimeout();
    },

    // ✅ NEW: remove only the items that are currently selected
    removeSelectedItems: (state, action) => {
      const userId = action.payload;

      // Create audit log for all current cart items before removing
      if (userId) {
        createCartAuditLog(state.items, userId);
      }

      // Filter out items where selected is explicitly true (handle undefined/null cases)
      state.items = state.items.filter((i) => i.selected !== true);
      state.lastModified = new Date().toISOString();
      saveCartToStorage(state.items, state.lastModified);
      syncToBackendWithTimeout();
    },

    openCartDrawer: (state) => {
      state.drawerOpen = true;
    },
    closeCartDrawer: (state) => {
      state.drawerOpen = false;
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  undoRemoveFromCart,
  clearCart,
  loadCart,
  setLoading,
  updateQuantity,
  setItemSubscription,
  setItemSelected,
  setItems,
  removeSelectedItems,
  openCartDrawer,
  closeCartDrawer,
} = cartSlice.actions;

// Export the storage helper function
export { loadCartFromStorage };

export default cartSlice.reducer;
